// Pure calculations for the Analytics page. Nothing here fetches data or
// changes values from the server; it only aggregates rows already loaded.
//
// Sources (backend: charging_sessions / payment_transactions tables):
//   session rows      -> cms_get_charging_sessions_by_group_id
//                        fields: id, vehicle_id, station_id, gun_id, group_id,
//                        session_start, session_end, duration_seconds,
//                        energy_kwh, total_cost, gst_amount, final_cost
//   transaction rows  -> cms_get_all_payment_transactions
//                        fields: transaction_type, status, amount, final_cost,
//                        station_id {Valid,String}, created_at
//
// Dates: the backend filters on the stored wall-clock timestamp, so rows are
// bucketed by the date/hour written in the timestamp string (no timezone
// conversion), which keeps chart buckets consistent with the server's filter.

const num = (v) => {
  const n = typeof v === "number" ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const nullable = (v) => (v && typeof v === "object" && "Valid" in v ? (v.Valid ? v.String : "") : v || "");

export const dateKey = (ts) => (typeof ts === "string" ? ts.slice(0, 10) : "");
const hourOf = (ts) => (typeof ts === "string" && ts.length >= 13 ? parseInt(ts.slice(11, 13), 10) : NaN);

// ---------- date ranges ----------

const toUTCDate = (key) => new Date(`${key}T00:00:00Z`);
const fmt = (d) => d.toISOString().slice(0, 10);

export const addDays = (key, days) => {
  const d = toUTCDate(key);
  d.setUTCDate(d.getUTCDate() + days);
  return fmt(d);
};

export const todayKey = () => {
  const d = new Date();
  // local calendar date, as the user sees it
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export const daysBetween = (fromKey, toKey) =>
  Math.round((toUTCDate(toKey) - toUTCDate(fromKey)) / 86400000) + 1;

// the equally long period right before the selected one
export const previousRange = ({ fromDate, toDate }) => {
  const len = daysBetween(fromDate, toDate);
  return { fromDate: addDays(fromDate, -len), toDate: addDays(fromDate, -1) };
};

export const listDays = (fromKey, toKey) => {
  const out = [];
  for (let k = fromKey; k <= toKey; k = addDays(k, 1)) out.push(k);
  return out;
};

// ---------- time buckets ----------

// Monday-based week start
const weekStart = (key) => {
  const d = toUTCDate(key);
  const day = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - day);
  return fmt(d);
};

export const bucketKey = (key, granularity) =>
  granularity === "monthly" ? key.slice(0, 7) : granularity === "weekly" ? weekStart(key) : key;

const shortDate = new Intl.DateTimeFormat("en-IN", { day: "numeric", month: "short", timeZone: "UTC" });
const monthLabel = new Intl.DateTimeFormat("en-IN", { month: "short", year: "numeric", timeZone: "UTC" });

export const bucketLabel = (bucket, granularity) => {
  if (granularity === "monthly") return monthLabel.format(toUTCDate(`${bucket}-01`));
  if (granularity === "weekly") return `Wk of ${shortDate.format(toUTCDate(bucket))}`;
  return shortDate.format(toUTCDate(bucket));
};

// ---------- sessions ----------

// A session counts as "with energy" when it delivered more than 0 kWh. The
// backend has no session status column, so failed / interrupted sessions
// cannot be identified; sessions with 0 kWh are reported separately instead.
export const sessionHasEnergy = (s) => num(s.energy_kwh) > 0;

export const filterSessions = (sessions, { stationId, groupId }) =>
  (sessions || []).filter(
    (s) => (!stationId || s.station_id === stationId) && (!groupId || String(s.group_id) === String(groupId))
  );

export const summarizeSessions = (sessions) => {
  const rows = sessions || [];
  let revenue = 0, gst = 0, energy = 0, duration = 0, durationCount = 0, withEnergy = 0;
  const vehicles = new Set();
  for (const s of rows) {
    revenue += num(s.final_cost);
    gst += num(s.gst_amount);
    energy += num(s.energy_kwh);
    const d = num(s.duration_seconds);
    if (d > 0) { duration += d; durationCount += 1; }
    if (sessionHasEnergy(s)) withEnergy += 1;
    if (s.vehicle_id) vehicles.add(s.vehicle_id);
  }
  const count = rows.length;
  return {
    revenue,
    gst,
    energy,
    sessions: count,
    sessionsWithEnergy: withEnergy,
    sessionsWithoutEnergy: count - withEnergy,
    vehicles: vehicles.size,
    avgRevenuePerSession: count ? revenue / count : 0,
    avgDurationSeconds: durationCount ? duration / durationCount : 0,
    avgEnergyPerSession: count ? energy / count : 0,
  };
};

// one entry per day in the range (days without sessions are 0), used for KPI sparklines
export const dailySeries = (sessions, fromKey, toKey) => {
  const map = new Map(listDays(fromKey, toKey).map((k) => [k, []]));
  for (const s of sessions || []) {
    const k = dateKey(s.session_start);
    if (map.has(k)) map.get(k).push(s);
  }
  return [...map.entries()].map(([day, rows]) => ({ day, ...summarizeSessions(rows) }));
};

// sessions/energy/revenue per day, week or month, with empty periods filled in
export const sessionTrend = (sessions, fromKey, toKey, granularity) => {
  const buckets = new Map();
  for (const day of listDays(fromKey, toKey)) {
    const b = bucketKey(day, granularity);
    if (!buckets.has(b)) buckets.set(b, []);
  }
  for (const s of sessions || []) {
    const k = dateKey(s.session_start);
    if (k < fromKey || k > toKey) continue;
    buckets.get(bucketKey(k, granularity))?.push(s);
  }
  return [...buckets.entries()].map(([bucket, rows]) => {
    const sum = summarizeSessions(rows);
    return {
      bucket,
      label: bucketLabel(bucket, granularity),
      revenue: sum.revenue,
      energy: sum.energy,
      sessions: sum.sessions,
      withEnergy: sum.sessionsWithEnergy,
      withoutEnergy: sum.sessionsWithoutEnergy,
      avgEnergy: sum.avgEnergyPerSession,
    };
  });
};

export const hourlyDistribution = (sessions) => {
  const hours = Array.from({ length: 24 }, (_, h) => ({ hour: h, label: `${String(h).padStart(2, "0")}:00`, sessions: 0, energy: 0 }));
  for (const s of sessions || []) {
    const h = hourOf(s.session_start);
    if (h >= 0 && h < 24) {
      hours[h].sessions += 1;
      hours[h].energy += num(s.energy_kwh);
    }
  }
  return hours;
};

export const stationPerformance = (sessions, stationNames = {}) => {
  const map = new Map();
  for (const s of sessions || []) {
    const id = s.station_id || "Unknown";
    if (!map.has(id)) map.set(id, { stationId: id, sessions: 0, energy: 0, revenue: 0, duration: 0, durationCount: 0, vehicles: new Set() });
    const row = map.get(id);
    row.sessions += 1;
    row.energy += num(s.energy_kwh);
    row.revenue += num(s.final_cost);
    const d = num(s.duration_seconds);
    if (d > 0) { row.duration += d; row.durationCount += 1; }
    if (s.vehicle_id) row.vehicles.add(s.vehicle_id);
  }
  return [...map.values()].map((r) => ({
    stationId: r.stationId,
    name: stationNames[r.stationId]?.name || "",
    location: stationNames[r.stationId]?.location || "",
    sessions: r.sessions,
    energy: r.energy,
    revenue: r.revenue,
    vehicles: r.vehicles.size,
    avgDurationSeconds: r.durationCount ? r.duration / r.durationCount : 0,
  }));
};

export const vehicleUsage = (sessions) => {
  const map = new Map();
  for (const s of sessions || []) {
    if (!s.vehicle_id) continue;
    if (!map.has(s.vehicle_id)) map.set(s.vehicle_id, { vehicleId: s.vehicle_id, sessions: 0, energy: 0, spend: 0, lastCharged: "", stations: new Set() });
    const v = map.get(s.vehicle_id);
    v.sessions += 1;
    v.energy += num(s.energy_kwh);
    v.spend += num(s.final_cost);
    if (s.station_id) v.stations.add(s.station_id);
    if (typeof s.session_start === "string" && s.session_start > v.lastCharged) v.lastCharged = s.session_start;
  }
  const vehicles = [...map.values()].map((v) => ({ ...v, stations: v.stations.size }));
  const total = vehicles.length;
  const repeat = vehicles.filter((v) => v.sessions >= 2).length;
  const totalSessions = vehicles.reduce((a, v) => a + v.sessions, 0);
  const bands = [
    { label: "1 session", min: 1, max: 1 },
    { label: "2–3", min: 2, max: 3 },
    { label: "4–10", min: 4, max: 10 },
    { label: "11–25", min: 11, max: 25 },
    { label: "26+", min: 26, max: Infinity },
  ].map((b) => ({ label: b.label, vehicles: vehicles.filter((v) => v.sessions >= b.min && v.sessions <= b.max).length }));
  return {
    vehicles,
    total,
    repeat,
    repeatShare: total ? repeat / total : 0,
    avgSessionsPerVehicle: total ? totalSessions / total : 0,
    energy: vehicles.reduce((a, v) => a + v.energy, 0),
    bands,
  };
};

// ---------- payment transactions ----------

// statuses that mean the money actually moved
export const SETTLED_STATUSES = ["completed", "captured"];

export const filterTransactions = (transactions, { stationId }) =>
  (transactions || []).filter((t) => !stationId || nullable(t.station_id) === stationId);

export const transactionBreakdown = (transactions) => {
  const byType = new Map();
  const byStatus = new Map();
  for (const t of transactions || []) {
    const type = t.transaction_type || "other";
    if (!byType.has(type)) byType.set(type, { type, count: 0, amount: 0, settledAmount: 0 });
    const row = byType.get(type);
    row.count += 1;
    row.amount += num(t.amount);
    if (SETTLED_STATUSES.includes(t.status)) row.settledAmount += num(t.amount);
    const status = t.status || "unknown";
    byStatus.set(status, (byStatus.get(status) || 0) + 1);
  }
  return {
    byType: [...byType.values()].sort((a, b) => b.count - a.count),
    byStatus: [...byStatus.entries()].map(([status, count]) => ({ status, count })).sort((a, b) => b.count - a.count),
    total: (transactions || []).length,
  };
};

// refunds per day/week/month: settled refund transactions only
export const refundTrend = (transactions, fromKey, toKey, granularity) => {
  const buckets = new Map();
  for (const day of listDays(fromKey, toKey)) buckets.set(bucketKey(day, granularity), 0);
  for (const t of transactions || []) {
    if (t.transaction_type !== "refund" || !SETTLED_STATUSES.includes(t.status)) continue;
    const k = dateKey(t.created_at);
    if (k < fromKey || k > toKey) continue;
    const b = bucketKey(k, granularity);
    if (buckets.has(b)) buckets.set(b, buckets.get(b) + num(t.amount));
  }
  return buckets;
};

// ---------- comparisons & formatting ----------

export const percentChange = (current, previous) => {
  if (!Number.isFinite(previous) || previous === 0) return current > 0 ? null : 0;
  return (current - previous) / previous;
};

export const formatDuration = (seconds) => {
  const s = Math.round(seconds || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  return `${m}m ${String(s % 60).padStart(2, "0")}s`;
};

const compactFmt = new Intl.NumberFormat("en-IN", { notation: "compact", maximumFractionDigits: 1 });
const numberFmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });
const decimalFmt = new Intl.NumberFormat("en-IN", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

export const formatCompact = (n) => compactFmt.format(n || 0);
export const formatNumber = (n) => numberFmt.format(n || 0);
export const formatDecimal = (n) => decimalFmt.format(n || 0);
export const formatCompactINR = (n) => `₹${compactFmt.format(n || 0)}`;

// ---------- settlements ----------

// per-session settlement rows (settlement_transactions, one per charging session);
// sessions without a row are reported as "no record"
export const SETTLEMENT_STATUSES = ["settled", "pending", "processing", "failed", "skipped"];

export const settlementSummary = (sessions) => {
  const byStatus = Object.fromEntries([...SETTLEMENT_STATUSES, "none"].map((s) => [s, { status: s, count: 0, amount: 0, sessionValue: 0 }]));
  for (const s of sessions || []) {
    const key = s.settlement_status && byStatus[s.settlement_status] ? s.settlement_status : s.settlement_status ? "skipped" : "none";
    byStatus[key].count += 1;
    byStatus[key].amount += num(s.settlement_amount);
    byStatus[key].sessionValue += num(s.final_cost);
  }
  return byStatus;
};
